import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import PageHeader, { KpiTile } from '../components/common/PageHeader';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { Checkbox, MonthInput, NativeSelect } from '../components/common/FormField';
import { compact, hasErrors, v } from '../utils/validation';
import GenerateInvoiceModal from '../components/billing/GenerateInvoiceModal';
import { InvoiceStatusBadge, STATUS_OPTIONS } from '../components/billing/invoiceStatus';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage, triggerBlobDownload } from '../services/api';
import { BillingInvoice, Client, CompanyProfile } from '../types';
import {
  currentMonth,
  financialYear,
  fmtCurrency,
  fmtDate,
  fmtMonth,
  fyBounds,
  recentFinancialYears,
  todayISO,
} from '../utils/format';

interface BulkResult {
  billingMonth: string;
  generated: { invoiceId: number; invoiceNumber: string; companyName: string; totalAmount: number }[];
  skipped: { clientId: number; companyName: string; reason: string }[];
}

export default function BillingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('billing:create');
  const canDownload = hasPermission('billing:download');

  const [fy, setFy] = useState(financialYear());
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showGenerate, setShowGenerate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkMonth, setBulkMonth] = useState(currentMonth());
  const [bulkDate, setBulkDate] = useState(todayISO());
  const [bulkMarkSent, setBulkMarkSent] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [bulkErrors, setBulkErrors] = useState<{ month?: string; date?: string }>({});
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const range = fyBounds(fy);
  const filters = {
    from: range.from,
    to: range.to,
    clientId: filterClientId || undefined,
    status: filterStatus || undefined,
  };

  const { data: clients = [] } = useQuery({
    queryKey: queryKeys.clients.all(),
    queryFn: () => api.get<Client[]>('/clients'),
  });
  const { data: company } = useQuery({
    queryKey: queryKeys.company(),
    queryFn: () => api.get<CompanyProfile | null>('/company-profile'),
  });
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: queryKeys.billing.invoices(filters),
    queryFn: () => api.get<BillingInvoice[]>('/billing/invoices', filters),
  });

  const bulkMutation = useMutation({
    mutationFn: () =>
      api.post<BulkResult>('/billing/generate-bulk', {
        billingMonth: bulkMonth,
        invoiceDate: bulkDate || undefined,
        markSent: bulkMarkSent,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.all() });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const totals = useMemo(() => {
    const live = invoices.filter((i) => i.status !== 'cancelled');
    const sum = (rows: BillingInvoice[]) => rows.reduce((s, i) => s + parseFloat(i.totalAmount), 0);
    return {
      billed: sum(live),
      received: live.reduce((s, i) => s + i.amountReceived, 0),
      outstanding: sum(live.filter((i) => i.status === 'sent')),
      overdue: sum(live.filter((i) => i.isOverdue)),
      overdueCount: live.filter((i) => i.isOverdue).length,
      draftCount: live.filter((i) => i.status === 'draft').length,
    };
  }, [invoices]);

  async function downloadPdf(invoice: BillingInvoice) {
    setDownloadingId(invoice.id);
    try {
      const blob = await api.download(`/billing/invoices/${invoice.id}/pdf`);
      triggerBlobDownload(blob, `${invoice.invoiceNumber.replace(/\//g, '-')}.pdf`);
    } catch (err) {
      showToast(errorMessage(err, 'Failed to download invoice'), 'error');
    } finally {
      setDownloadingId(null);
    }
  }

  async function runBulk() {
    const e = compact({
      month: v.month(bulkMonth, 'Billing month'),
      date: v.date(bulkDate, 'Invoice date', { notFuture: true }),
    });
    setBulkErrors(e);
    if (hasErrors(e)) return;
    try {
      const result = await bulkMutation.mutateAsync();
      setBulkResult(result);
      showToast(`${result.generated.length} invoice(s) generated for ${fmtMonth(result.billingMonth)}`, result.generated.length ? 'success' : 'info');
    } catch (err) {
      showToast(errorMessage(err, 'Monthly run failed'), 'error');
    }
  }

  const companyIncomplete = company && (!company.bankName || !company.bankAccountNo || !company.bankIfsc);

  const columns = [
    {
      key: 'invoiceNumber',
      label: 'Invoice #',
      render: (_: any, row: BillingInvoice) => (
        <span className="font-mono text-sm font-semibold text-brand-700">{row.invoiceNumber}</span>
      ),
    },
    {
      key: 'client',
      label: 'Client',
      render: (_: any, row: BillingInvoice) => (
        <span className="text-sm text-ink-800">{row.billTo?.companyName ?? row.client?.companyName}</span>
      ),
    },
    {
      key: 'invoiceDate',
      label: 'Date',
      render: (_: any, row: BillingInvoice) => <span className="text-sm text-ink-600">{fmtDate(row.invoiceDate)}</span>,
    },
    {
      key: 'period',
      label: 'Type / Period',
      render: (_: any, row: BillingInvoice) => (
        <div className="text-xs text-ink-600">
          <span className="font-semibold capitalize text-ink-700">{row.invoiceType}</span>
          {row.billingPeriodStart && (
            <span className="block">
              {row.invoiceType === 'subscription'
                ? fmtMonth(row.billingPeriodStart.slice(0, 7))
                : `${fmtDate(row.billingPeriodStart)} – ${fmtDate(row.billingPeriodEnd)}`}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'totalAmount',
      label: 'Total',
      render: (_: any, row: BillingInvoice) => (
        <div>
          <span className="text-sm font-semibold text-ink-900">{fmtCurrency(row.totalAmount)}</span>
          <span className="block text-[11px] text-ink-400">excl. GST {fmtCurrency(row.subtotal)}</span>
        </div>
      ),
    },
    {
      key: 'dueDate',
      label: 'Due',
      render: (_: any, row: BillingInvoice) => (
        <span className={`text-xs ${row.isOverdue ? 'text-red-600 font-semibold' : 'text-ink-500'}`}>
          {row.status === 'paid' ? `Paid ${fmtDate(row.paidAt)}` : fmtDate(row.dueDate)}
        </span>
      ),
    },
    { key: 'status', label: 'Status', render: (_: any, row: BillingInvoice) => <InvoiceStatusBadge invoice={row} /> },
    {
      key: 'actions',
      label: 'Actions',
      width: '190px',
      render: (_: any, row: BillingInvoice) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-3 py-1 bg-ink-100 text-ink-700 text-xs rounded hover:bg-ink-200 flex items-center whitespace-nowrap"
            onClick={(e) => { e.stopPropagation(); navigate(`/billing/invoices/${row.id}`); }}
          >
            <i className="fas fa-eye mr-1" />View
          </button>
          {canDownload && (
            <button
              type="button"
              className="px-3 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 disabled:opacity-60 flex items-center whitespace-nowrap"
              disabled={downloadingId === row.id}
              onClick={(e) => { e.stopPropagation(); downloadPdf(row); }}
            >
              <i className={`fas ${downloadingId === row.id ? 'fa-spinner fa-spin' : 'fa-download'} mr-1`} />PDF
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Billing"
          subtitle={`Generate and track client invoices — ${invoices.length} in FY ${fy}`}
          actions={
            canCreate && (
              <>
                <Button variant="outline" icon="fa-calendar-check" onClick={() => { setBulkResult(null); setBulkErrors({}); setShowBulk(true); }}>
                  Monthly Run
                </Button>
                <Button variant="primary" icon="fa-plus" onClick={() => setShowGenerate(true)}>
                  Generate Invoice
                </Button>
              </>
            )
          }
        />

        {companyIncomplete && (
          <div className="mb-4 rounded-lg bg-accent-50 border border-accent-200 px-4 py-3 text-sm text-accent-800 flex flex-wrap items-center justify-between gap-2">
            <span><i className="fas fa-triangle-exclamation mr-2" />Add your bank details in Company Profile before generating invoices — they are printed on every tax invoice.</span>
            <button className="font-semibold underline" onClick={() => navigate('/settings?tab=company')}>Open Settings</button>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 lg:mb-6">
          <KpiTile label="Billed (incl. GST)" value={fmtCurrency(totals.billed, 0)} icon="fa-file-invoice" tone="brand" hint={`${totals.draftCount} draft`} />
          <KpiTile label="Received" value={fmtCurrency(totals.received, 0)} icon="fa-circle-check" tone="green" />
          <KpiTile label="Outstanding" value={fmtCurrency(totals.outstanding, 0)} icon="fa-hourglass-half" tone="accent" hint="Sent, not yet paid" />
          <KpiTile label="Overdue" value={fmtCurrency(totals.overdue, 0)} icon="fa-triangle-exclamation" tone="red" hint={`${totals.overdueCount} invoice(s)`} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 lg:mb-6">
          <NativeSelect value={fy} onChange={setFy}
            options={recentFinancialYears().map((y) => ({ value: y, label: `FY ${y}` }))} />
          <NativeSelect value={filterClientId} onChange={setFilterClientId} placeholder="All Clients"
            options={clients.map((c) => ({ value: c.id, label: c.companyName }))} />
          <NativeSelect value={filterStatus} onChange={setFilterStatus} placeholder="All Statuses" options={STATUS_OPTIONS} />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-ink-200 overflow-visible lg:overflow-hidden">
          <div className="px-1 py-3 lg:p-0">
            <ResponsiveTable
              columns={columns}
              data={invoices}
              isLoading={isLoading}
              height="calc(100vh - 400px)"
              emptyMessage="No invoices found"
              onRowClick={(row: BillingInvoice) => navigate(`/billing/invoices/${row.id}`)}
            />
          </div>
        </div>

        <GenerateInvoiceModal
          isOpen={showGenerate}
          onClose={() => setShowGenerate(false)}
          clients={clients}
          company={company}
          onGenerated={(invoice) => {
            setShowGenerate(false);
            navigate(`/billing/invoices/${invoice.id}`);
          }}
        />

        {/* Monthly run — one subscription invoice per client */}
        <Modal
          isOpen={showBulk}
          onClose={() => setShowBulk(false)}
          title="Monthly Subscription Run"
          size="lg"
          footer={
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <Button variant="outline" onClick={() => setShowBulk(false)}>{bulkResult ? 'Close' : 'Cancel'}</Button>
              {!bulkResult && (
                <Button variant="primary" icon="fa-play" onClick={runBulk} disabled={bulkMutation.isPending || !bulkMonth}>
                  {bulkMutation.isPending ? 'Generating…' : 'Generate All'}
                </Button>
              )}
            </div>
          }
        >
          {!bulkResult ? (
            <div className="space-y-4">
              <p className="text-sm text-ink-600">
                Creates one invoice for every client with an active subscription in the chosen month.
                Clients already invoiced for that month are skipped, so it is safe to run again.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MonthInput label="Billing Month" required value={bulkMonth} error={bulkErrors.month}
                  onChange={(val) => { setBulkMonth(val); setBulkErrors((p) => ({ ...p, month: undefined })); }} />
                <Input label="Invoice Date" type="date" required value={bulkDate} max={todayISO()} error={bulkErrors.date}
                  onChange={(e) => { setBulkDate(e.target.value); setBulkErrors((p) => ({ ...p, date: undefined })); }} />
              </div>
              <Checkbox checked={bulkMarkSent} onChange={setBulkMarkSent} label="Mark invoices as Sent"
                hint="Leave unticked to review them as drafts first" />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">
                  Generated ({bulkResult.generated.length})
                </p>
                {bulkResult.generated.length === 0 && <p className="text-sm text-ink-500">Nothing new to generate for {fmtMonth(bulkResult.billingMonth)}.</p>}
                <ul className="divide-y divide-ink-100">
                  {bulkResult.generated.map((g) => (
                    <li key={g.invoiceId} className="py-2 flex items-center justify-between gap-3 text-sm">
                      <button className="text-left hover:underline" onClick={() => navigate(`/billing/invoices/${g.invoiceId}`)}>
                        <span className="font-mono font-semibold text-brand-700">{g.invoiceNumber}</span>
                        <span className="text-ink-600"> — {g.companyName}</span>
                      </button>
                      <span className="font-semibold text-ink-900">{fmtCurrency(g.totalAmount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {bulkResult.skipped.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">Skipped ({bulkResult.skipped.length})</p>
                  <ul className="divide-y divide-ink-100">
                    {bulkResult.skipped.map((s) => (
                      <li key={s.clientId} className="py-2 text-sm">
                        <span className="text-ink-800">{s.companyName}</span>
                        <span className="block text-xs text-ink-500">{s.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </Layout>
  );
}
